import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/api/client';
import { FieldError } from './form-utils';
import { errorClass, useFieldErrors } from './use-field-errors';

export default function MediaForm({ media, onClose }) {
  const queryClient = useQueryClient();
  const { fieldErrors, handleError, clearErrors } = useFieldErrors();

  const [formData, setFormData] = useState({
    name: media?.name ?? '',
    type: media?.type ?? 'SHIPPING_LABEL',
    width: media?.width ?? '',
    height: media?.height ?? '',
  });

  const saveMutation = useMutation({
    mutationFn: (data) =>
      media ? api.entities.Media.update(media.id, data) : api.entities.Media.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      toast.success(media ? 'Media updated' : 'Media added');
      onClose();
    },
    onError: (error) => handleError(error, 'Failed to save media'),
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    clearErrors();
    saveMutation.mutate(formData);
  };

  const set = (key) => (event) => setFormData({ ...formData, [key]: event.target.value });

  return (
    <Card className="border-amber-200 shadow-md">
      <CardHeader className="bg-amber-50 border-b">
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg">{media ? 'Edit media' : 'Add media'}</CardTitle>
          <Button type="button" variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="media-name">Media name</Label>
            <Input
              id="media-name"
              value={formData.name}
              onChange={set('name')}
              placeholder="e.g. #10 Envelope, 4x6 Label"
              className={errorClass(fieldErrors.name)}
              required
            />
            <FieldError message={fieldErrors.name} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="media-type">Media type</Label>
            <Select
              value={formData.type}
              onValueChange={(value) => setFormData({ ...formData, type: value })}
            >
              <SelectTrigger id="media-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SHIPPING_LABEL">Shipping label</SelectItem>
                <SelectItem value="ENVELOPE">Envelope</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              {formData.type === 'ENVELOPE'
                ? 'Envelopes offer USPS rates only — no other carrier sells postage you can print onto an envelope.'
                : 'All carriers are available for shipping labels.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              ['width', 'Width (in)', '4'],
              ['height', 'Height (in)', '6'],
            ].map(([key, label, placeholder]) => (
              <div key={key} className="space-y-2">
                <Label htmlFor={`media-${key}`}>{label}</Label>
                <Input
                  id={`media-${key}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData[key]}
                  onChange={set(key)}
                  placeholder={placeholder}
                  className={errorClass(fieldErrors[key])}
                  required
                />
                <FieldError message={fieldErrors[key]} />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saveMutation.isPending}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saveMutation.isPending ? 'Saving…' : 'Save media'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
